import { type Company, type TransitOperator } from '@opentripplanner/types';
import { Mode, Qualifier } from '#/types/otp2types.generated';
import {
  SCOOTER_CATEGORY_ID,
  TRIMET_DATE_FORMAT,
  TRIMET_TIME_FORMAT,
} from '#/shared/constants';

export const TRIMET_SHORT_NAME_ID = 'triMetShortNameId';

// TODO PUBWEB-3869: Many of these types are declared in OTP; we should use these where possible

export interface MapSymbol {
  maxZoom: number;
  minZoom: number;
  type: 'circle' | 'marker' | 'hubAndFloatingBike';
  pixels?: number;
  fillColor?: string;
  strokeColor?: string;
  dockStrokeColor?: string;
}

export interface Otpv1Company extends Company {
  mapSymbols?: MapSymbol[];
}

interface ModeOption {
  mode?: string;
  company?: string;
  label?: string;
}

type HiddenOrShortNameRequired =
  | { [TRIMET_SHORT_NAME_ID]: string; hidden?: never }
  | { [TRIMET_SHORT_NAME_ID]?: never; hidden: true };

type TransitMode = {
  mode: Mode;
  labelId: string;
  qualifiers?: Qualifier[]; // Add optional qualifiers array
} & HiddenOrShortNameRequired;

interface Category {
  id: string;
  type: 'access' | 'exclusive';
  mode?: string;
  options?: ModeOption[];
}

interface OtpConfig {
  transitOperators: TransitOperator[];
  dateTime: {
    timeFormat: string;
    dateFormat: string;
  };
  modes: {
    transitModes: TransitMode[];
    categories: Category[];
  };
  companies: Otpv1Company[];
  otp2ApiCompanies: Company[];
}

/**
 * A config object for `otp-ui` components, the schema for this object is
 * defined here: https://github.com/opentripplanner/otp-ui/blob/master/packages/types/src/index.ts#L143
 */
const config: OtpConfig = {
  transitOperators: [
    {
      agencyId: 'TRIMET:TRIMET',
      feedId: 'TRIMET',
      name: 'TriMet',
    },
    {
      agencyId: 'TRIMET:PSC',
      feedId: 'TRIMET',
      name: 'Portland Streetcar',
    },
    {
      agencyId: 'TRIMET:TRAM',
      feedId: 'TRIMET',
      name: 'Portland Aerial Tram',
    },
    {
      agencyId: 'SMART:214',
      feedId: 'SMART',
      name: 'SMART',
    },
    {
      agencyId: 'MULT:162',
      feedId: 'MULT',
      name: 'Multnomah County Transit',
    },
    {
      agencyId: 'SAM:86',
      feedId: 'SAM',
      name: 'Sandy Area Metro',
    },
    {
      agencyId: 'CLACKAMAS:1807',
      feedId: 'CLACKAMAS',
      name: 'Clackamas County Social Services',
    },
    {
      agencyId: 'WAPARK:501',
      feedId: 'WAPARK',
      name: 'Washington Park Shuttle',
    },
    {
      agencyId: 'RIDECONNECTION:133',
      feedId: 'RIDECONNECTION',
      name: 'Ride Connection',
    },
    {
      agencyId: 'CTRAN:C-TRAN',
      feedId: 'CTRAN',
      name: 'C-TRAN',
    },
    {
      agencyId: 'CTRAN_FLEX:a8b6ef46-7d4d-45f8-8200-cf4f5ce9d5a6',
      feedId: 'CTRAN_FLEX',
      name: 'The Current by C-TRAN',
    },
  ],
  dateTime: {
    timeFormat: TRIMET_TIME_FORMAT,
    dateFormat: TRIMET_DATE_FORMAT,
  },
  modes: {
    transitModes: [
      {
        mode: Mode.Bus,
        labelId: 'planner.bus-mode-label',
        [TRIMET_SHORT_NAME_ID]: 'planner.bus-mode-short-name',
      },
      {
        mode: Mode.Tram,
        labelId: 'planner.light-rail-mode-label',
        [TRIMET_SHORT_NAME_ID]: 'planner.light-rail-mode-short-name',
      },
      {
        mode: Mode.Rail,
        labelId: 'planner.rail-mode-label',
        [TRIMET_SHORT_NAME_ID]: 'planner.rail-mode-short-name',
      },
      {
        mode: Mode.Gondola,
        labelId: 'planner.gondola-mode-label',
        hidden: true,
      },
      {
        mode: Mode.Flex,
        labelId: 'planner.flex-mode-label',
        hidden: true,
        qualifiers: [Qualifier.Access, Qualifier.Egress, Qualifier.Direct],
      },
    ],
    categories: [
      {
        id: 'BICYCLE',
        type: 'access',
        options: [
          {
            mode: 'BICYCLE',
          },
          {
            mode: 'BICYCLE_RENT',
            company: 'BIKETOWN',
            label: 'BIKETOWN',
          },
        ],
      },
      {
        id: 'CAR',
        type: 'access',
        options: [
          {
            mode: 'CAR_PARK',
          },
        ],
      },
      {
        id: SCOOTER_CATEGORY_ID,
        type: 'access',
        mode: 'MICROMOBILITY_RENT',
        options: [
          {
            label: 'Lime',
            company: 'LIME',
          },
        ],
      },
    ],
  },
  // A list of private transportation operators. These are either companies
  // that provide rental vehicles or transportation network companies. Companies
  // that have multiple modes of transport should have all modes listed as a
  // string with commas (e.g., BICYCLE_RENT, MICROMOBILITY_RENT, ...)
  companies: [
    {
      id: 'BIKETOWN',
      label: 'Biketown',
      modes: 'BICYCLE_RENT',
      mapSymbols: [
        {
          maxZoom: 13,
          minZoom: 0,
          type: 'circle',
          pixels: 4,
          fillColor: '#FF2E28',
          dockStrokeColor: '#000000',
        },
        {
          maxZoom: 17,
          minZoom: 14,
          type: 'circle',
          pixels: 6,
          fillColor: '#FF2E28',
          dockStrokeColor: '#000000',
        },
        {
          maxZoom: 999,
          minZoom: 18,
          type: 'hubAndFloatingBike',
        },
      ],
    },
    {
      id: 'LIME',
      label: 'Lime',
      modes: 'MICROMOBILITY_RENT',
      mapSymbols: [
        {
          maxZoom: 13,
          minZoom: 0,
          type: 'circle',
          pixels: 4,
          fillColor: '#656565',
          strokeColor: '#CCCCCC',
        },
        {
          maxZoom: 17,
          minZoom: 14,
          type: 'circle',
          pixels: 6,
          fillColor: '#656565',
          strokeColor: '#CCCCCC',
        },
        {
          maxZoom: 999,
          minZoom: 18,
          fillColor: '#656565',
          type: 'marker',
        },
      ],
    },
    {
      id: 'ZIPCAR',
      label: 'Zipcar',
      modes: 'CAR_RENT',
    },
  ],
  otp2ApiCompanies: [
    {
      id: 'LIME',
      label: 'Lime',
      modes: 'SCOOTER',
    },
    {
      id: 'lyft_pdx',
      label: 'BIKETOWN',
      modes: 'SCOOTER, BICYCLE',
    },
  ],
};

export default config;
